import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Lock } from "lucide-react";

import { useI18n } from "@/i18n/I18nContext";
import { hasErrors } from "@/lib/memberForms";
import { VISIBILITY, profileDraft, profileErrors, profileChanges, readVisibility, editableFields, previewVisibility, changeLabel, visibilityLabel } from "@/lib/v3Pass";
import { useMemberAuth } from "../MemberAuthContext";
import { useMemberProfile } from "../useMemberProfile";
import { shortName } from "../navSections";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, Avatar } from "../components/kit";
import { V3Title, V3Columns, V3Field, V3Actions, StatusBanner, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { V3Select, AreaCard, ResponsibleCard, V3_CARD, V3_LINK, V3_SMALL } from "../components/v3app";

const LOCKED = "Ändern ist möglich, Speichern kann der gemeinsame Core noch nicht.";

function visibilityHint(prof, supported) {
  if (supported) return undefined;
  return prof.prefs?.privacy?.discoverable === false
    ? "Je Feld noch nicht einstellbar. Dein Profil erscheint derzeit nicht bei „Menschen entdecken“."
    : "Je Feld noch nicht einstellbar. Derzeit sehen alle angemeldeten Mitglieder dein Profil.";
}

// What another member would see; "Nur ich" hides the field, "Bestätigte Kontakte" says so instead of the value.
function MemberPreview({ draft, prof }) {
  const { t } = useI18n();
  const vis = previewVisibility(draft, prof);
  const shown = (k, value) => (vis[k] === "only_me" ? null : vis[k] === "contacts" ? <span className="italic text-v3-muted">Nur für bestätigte Kontakte sichtbar</span> : value);
  const interests = (prof.interests || []).map((k) => t(`member.categories.${k}`)).join(" · ");
  return (
    <div className={`${V3_CARD} px-[20px] py-[18px]`} data-testid="profile-member-preview">
      <div className="flex items-center gap-[16px]">
        <Avatar name={vis.name === "members" ? draft.name : ""} color={prof.avatar_color} size="h-[54px] w-[54px] text-sm" />
        <div className="min-w-0">
          <p className="break-words text-[19px] font-bold text-v3-ink" data-testid="profile-preview-name">{shown("name", draft.name) || "Name ausgeblendet"}</p>
          {draft.city && shown("city", draft.city) && <p className="text-[14px] text-v3-muted" data-testid="profile-preview-city">{shown("city", draft.city)}</p>}
        </div>
      </div>
      {draft.bio && shown("bio", draft.bio) && <p className="mt-[14px] whitespace-pre-wrap break-words text-[15px] leading-[22px] text-v3-ink" data-testid="profile-preview-bio">{shown("bio", draft.bio)}</p>}
      {interests && shown("interests", interests) && <p className="mt-[10px] text-[14px] text-v3-muted" data-testid="profile-preview-interests">Interessen: {shown("interests", interests)}</p>}
    </div>
  );
}

function PreviewStep({ draft, setDraft, prof, p, onBack }) {
  const { refresh } = useMemberAuth();
  const { changed, blocked, body } = profileChanges(draft, prof);
  const base = profileDraft(prof);
  const discard = () => setDraft((d) => ({ ...d, ...Object.fromEntries(blocked.map((k) => [k, base[k]])) }));
  const save = async () => { if (await p.patch(body, "Profil gespeichert.")) refresh().catch(() => {}); };
  const value = (k) => (k === "visibility" ? visibilityLabel(draft.visibility) : draft[k] || "Leer");
  return (<>
    <V3Title title="Mitgliedervorschau prüfen" intro="So sehen angemeldete Mitglieder dein Profil nach dem Speichern." testid="member-profile-title" />
    <V3Columns layout="app" aside={<><AreaCard title="Ausgewählter Vorgang" text="Mitgliedervorschau" testid="profile-area" /><ResponsibleCard /></>} main={
      <div data-testid="profile-preview-step" className="space-y-[16px]">
        <MemberPreview draft={draft} prof={prof} />
        {changed.length ? <DetailList>{changed.map((k) => <DetailRow key={k} label={`${changeLabel(k)} · geändert`} value={value(k)} testid={`profile-change-${k}`} />)}</DetailList>
          : <p className="text-[14px] text-v3-muted" data-testid="profile-no-changes">Keine Änderungen – es gibt nichts zu speichern.</p>}
        {blocked.length > 0 && (
          <div data-testid="profile-save-blocked">
            <StatusBanner tone="notice" icon={Lock} title="Noch nicht speicherbar" text={`${blocked.map(changeLabel).join(", ")}: Speichern ist im gemeinsamen Core noch nicht eingerichtet. Davon wird nichts übernommen.`} />
            <button type="button" onClick={discard} data-testid="profile-discard-blocked" className={V3_SMALL}>Nicht speicherbare Änderungen verwerfen</button>
          </div>
        )}
        <V3Actions>
          <button type="button" onClick={save} disabled={!changed.length || blocked.length > 0 || p.saving} data-testid="member-profile-save" className={V3_PRIMARY}>{p.saving ? "Wird gespeichert …" : "Änderungen speichern"}</button>
          <button type="button" onClick={onBack} disabled={p.saving} data-testid="profile-back-to-edit" className={V3_SECONDARY}>Weiter bearbeiten</button>
        </V3Actions>
      </div>
    } />
  </>);
}

function ProfileEditor({ p }) {
  const prof = p.data.profile;
  const [draft, setDraft] = useState(() => profileDraft(prof));
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState("edit");
  const editable = editableFields(prof);
  const vis = readVisibility(prof);
  const set = (k) => (e) => { const v = e.target.value; setDraft((d) => ({ ...d, [k]: v })); setErrors({}); };
  const review = (e) => { e.preventDefault(); const er = profileErrors(draft); setErrors(er); if (!hasErrors(er)) setStep("preview"); };
  if (step === "preview") return <div data-testid="member-profile" data-m="M18"><PreviewStep draft={draft} setDraft={setDraft} prof={prof} p={p} onBack={() => setStep("edit")} /></div>;
  return (
    <div data-testid="member-profile" data-m="M18">
      <V3Title title="Mein Profil bearbeiten" intro="Bearbeite dein Profil und prüfe anschliessend die Mitgliedervorschau." testid="member-profile-title" />
      <V3Columns layout="app" aside={<><AreaCard title="Ausgewählter Vorgang" text={shortName(prof.name) || "Dein Profil"} testid="profile-area" /><ResponsibleCard /></>} main={
        <form onSubmit={review} noValidate data-testid="member-profile-form">
          <V3Field id="profile-name" label="Anzeigename" value={draft.name} onChange={set("name")} error={errors.name} autoComplete="nickname" hint={editable.includes("name") ? undefined : LOCKED} />
          <V3Field id="profile-city" label="Stadt" value={draft.city} onChange={set("city")} error={errors.city} autoComplete="address-level2" hint={editable.includes("city") ? undefined : LOCKED} />
          <V3Field id="profile-bio" label="Kurz über mich" multiline rows={1} value={draft.bio} onChange={set("bio")} error={errors.bio} className="h-[65px] lg:h-[52px]" />
          <V3Select id="profile-visibility" label="Profil-Sichtbarkeit" value={draft.visibility} onChange={set("visibility")} testid="profile-visibility" hint={visibilityHint(prof, vis.supported)}>
            {VISIBILITY.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            {draft.visibility === "mixed" && <option value="mixed" disabled>Je Feld unterschiedlich</option>}
          </V3Select>
          <V3Actions className="mt-[8px] lg:mt-[8px]">
            <button type="submit" data-testid="member-profile-preview" className={V3_PRIMARY}>Profilvorschau ansehen</button>
            <button type="button" onClick={() => { setDraft(profileDraft(prof)); setErrors({}); }} data-testid="member-profile-cancel" className={V3_SECONDARY}>Abbrechen</button>
          </V3Actions>
          <div className="mt-[18px] flex flex-wrap gap-x-[24px]">
            <Link to="/profil/sichtbarkeit" data-testid="member-profile-visibility-link" className={`${V3_LINK} gap-2 text-[14px]`}><Eye className="h-4 w-4" aria-hidden="true" />Sichtbarkeit je Feld</Link>
            <Link to="/profil/foto" data-testid="member-profile-photo-link" className={`${V3_LINK} text-[14px]`}>Profilfoto ändern</Link>
            <Link to="/einrichtung/interessen" data-testid="member-profile-interests-link" className={`${V3_LINK} text-[14px]`}>Interessen ändern</Link>
          </div>
        </form>
      } />
    </div>
  );
}

// M18: edit form as in the template; only fields the Core can store are ever sent (contract C.2).
export default function Profile() {
  const p = useMemberProfile();
  if (p.error) return <ConnectionError onRetry={p.load} />;
  if (!p.data) return <Loading />;
  return <ProfileEditor key={JSON.stringify(profileDraft(p.data.profile))} p={p} />;
}
